// packages/core/src/learning/schools/inequality-awareness.ts
// ─────────────────────────────────────────────────────────────────────────
// Authored exhibit for "Inequality awareness". Matches boglehead-indexing.ts
// gold standard.
//
// ID-bound fields (the validator + compiler guard these):
//   - figures:        FigureId[]  — empty in source AND kept []. The worldview
//                     names no figures. Canon authors (Thomas Piketty, Matthew
//                     Desmond, Mehrsa Baradaran, Barbara Ehrenreich) are NOT
//                     figures-tally entries per the protocol, and none
//                     currently exist as a FigureId in figureRegistry.ts. This
//                     school contributes nothing to the running figure tally.
//   - applyActions:   ApplyAction union — type-checked at compile time. Only
//                     live/allowed action types emitted.
//   - featuredLessonIds / debateIds — [] until the cross-ref pass.
// ─────────────────────────────────────────────────────────────────────────

import type { SchoolExhibitEntry } from '../SchoolExhibitMeta';

export const INEQUALITY_AWARENESS_EXHIBIT: SchoolExhibitEntry = {
	id: 'inequality-awareness',
	tagline:
		'Inequality awareness believes starting lines shape money outcomes.',
	worldviewSummary:
		'Inequality awareness is paying attention to the forces that make money harder or easier before personal choices even begin. This lens protects you from pretending everyone starts with the same family support, wages, housing costs, education access, health burden, network, time, or safety. It accepts that personal agency still matters, but agency does not happen in a vacuum. The blind spot is turning structural awareness into defeatism, where every choice feels pointless. This lens is strongest when it helps users see unfair terrain clearly while still building the next real option.',
	coreBeliefs: [
		'Inequality awareness believes starting conditions shape the choices people can realistically make.',
		'Inequality awareness believes wages, housing, education, health, family support, and geography affect financial outcomes.',
		'Inequality awareness believes luck and timing are real forces, not personal virtue.',
		'Inequality awareness believes personal responsibility matters more when the system is described honestly.',
		'Inequality awareness believes shame is a lazy way to explain complex money outcomes.',
		'Inequality awareness believes good financial education should name barriers without making people feel powerless.',
	],
	warnsAgainst: [
		'Pretending everyone starts from the same place.',
		'Using personal finance advice to shame people for structural problems.',
		'Ignoring how housing, wages, health costs, and family obligations change the money math.',
		'Turning inequality into hopelessness instead of sharper judgment.',
	],
	helps: 'This lens helps people who feel behind, support family, face high housing costs, start without a safety net, or want a fairer view of money outcomes. It may not fit when someone uses structural barriers to avoid every practical next step.',
	figures: [],
	canon: [
		{
			title: 'Capital in the Twenty First Century',
			author: 'Thomas Piketty',
			oneLineIdea:
				'Wealth can compound across generations, which means starting points matter more than people like to admit.',
		},
		{
			title: 'Evicted',
			author: 'Matthew Desmond',
			oneLineIdea:
				'Housing instability can drain money, time, attention, and dignity before better choices are even available.',
		},
		{
			title: 'The Color of Money',
			author: 'Mehrsa Baradaran',
			oneLineIdea:
				'Access to banking, credit, and wealth building systems has never been evenly distributed.',
		},
		{
			title: 'Nickel and Dimed',
			author: 'Barbara Ehrenreich',
			oneLineIdea:
				'Low wage work can demand discipline and still leave people with little room to build stability.',
		},
		{
			title: 'Starting line framework',
			oneLineIdea:
				'A money plan should account for the support, risk, and obligations a person begins with.',
		},
	],
	featuredLessonIds: [],
	nativeModuleId: 'school-inequality-awareness',
	debateIds: [],
	applyActions: [
		{
			label: 'See how your starting point shows up in your current picture',
			action: {
				type: 'openDashboard',
				label: 'Review your current picture',
			},
		},
		{
			label: 'Test how higher housing or family costs change your path',
			action: {
				type: 'openSimulator',
				label: 'Simulate real life pressure',
				scenarioType: 'custom',
			},
		},
		{
			label: 'Check how much runway your current support system gives you',
			action: {
				type: 'calculateRunway',
				label: 'Check your runway',
			},
		},
		{
			label: 'Name the barrier without surrendering your next move',
			action: {
				type: 'noToolAction',
				label: 'Name barrier and move',
				prompt: 'Write one structural barrier affecting your money, then write one practical move that still remains available.',
			},
		},
	],
	marketingHooks: [
		{
			line: 'Not everyone starts at zero. Some people start below the floor.',
			kind: 'hook',
		},
		{
			line: 'The hidden cost of inequality is the option you never got to consider.',
			kind: 'hook',
		},
		{
			line: 'What financial advice sounds good until you add rent, family pressure, or no safety net?',
			kind: 'interview-question',
		},
		{
			line: 'When did you realize your starting line was different from someone else’s?',
			kind: 'interview-question',
		},
		{
			line: 'Nisk teaches money without pretending every adult starts with the same backup plan.',
			kind: 'kickstarter-beat',
		},
		{
			line: 'Financial education gets stronger when it names both agency and unfair terrain.',
			kind: 'kickstarter-beat',
		},
		{
			line: 'Ask people what helped them get ahead that was not visible on their resume.',
			kind: 'creator-prompt',
		},
		{
			line: 'Show how the same income creates different outcomes when one person has family support and one does not.',
			kind: 'creator-prompt',
		},
	],
	revision: 1,
};
